import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { useModules } from "@/auth/ModulesContext";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-public";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { isIncome, sortBy } from "@/lib/accounting-utils";

type ProductResponse = components["schemas"]["ProductResponse"];
type CurrencyResponse = components["schemas"]["CurrencyResponse"];

export interface EditProductDialogProps {
  product: ProductResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: () => void;
}

const DEFAULT_ACCOUNT = "__default__";

export function EditProductDialog({
  product,
  open,
  onOpenChange,
  onUpdated,
}: EditProductDialogProps) {
  const api = useDolfinClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [currency, setCurrency] = useState("");
  const [type, setType] = useState<"Product" | "Service">("Product");
  // Offered only with accounting sync and a synced chart; otherwise carried through, because PUT replaces every field.
  const [incomeAccountId, setIncomeAccountId] = useState<string | null>(null);
  const [ledgerAccounts, setLedgerAccounts] = useState<components["schemas"]["LedgerAccountResponse"][]>([]);
  const { hasModule, modules } = useModules();
  const accountingEnabled = modules !== null && hasModule("ar.accounting");
  const [currencies, setCurrencies] = useState<CurrencyResponse[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !accountingEnabled) return;
    api.GET("/v1/accounting/ledger-accounts", {}).then(({ data }) => setLedgerAccounts(sortBy(data ?? [], isIncome)));
  }, [api, open, accountingEnabled]);

  useEffect(() => {
    if (product) {
      setName(product.name ?? "");
      setDescription(product.description ?? "");
      setUnitPrice(String(product.unitPrice ?? ""));
      setCurrency(product.currency ?? "");
      setType((product.type as "Product" | "Service") ?? "Product");
      setIncomeAccountId(product.incomeLedgerAccountId ?? null);
      setError(null);
    }
  }, [product]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.GET("/v1/currencies/available").then((res) => {
      if (cancelled) return;
      setCurrencies((res.data as CurrencyResponse[]) ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setSubmitting(false);
      setError(null);
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!product) return;
    setError(null);

    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!currency) {
      setError("Currency is required.");
      return;
    }
    // Zero is a valid price, but blank is an error, not a silent zero: Number("") is 0, so test the string first.
    if (unitPrice.trim() === "") {
      setError("Unit price is required. Enter 0 for a free item.");
      return;
    }

    const priceNum = Number(unitPrice);
    if (isNaN(priceNum) || priceNum < 0) {
      setError("Unit price must be zero or more.");
      return;
    }

    setSubmitting(true);
    const { error: apiError } = await api.PUT("/v1/products/{id}", {
      params: { path: { id: product.id! } },
      body: {
        name: name.trim(),
        description: description.trim() || null,
        unitPrice: priceNum,
        currency,
        type,
        incomeLedgerAccountId: incomeAccountId,
      },
    });

    if (apiError) {
      const message = apiErrorMessage(apiError, "Failed to update product.");
      setError(message);
      toast.error(message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    onUpdated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
            <DialogDescription>Update product details.</DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-product-name">Name</Label>
              <Input
                id="edit-product-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-product-description">Description</Label>
              <Textarea
                id="edit-product-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-product-price">Unit Price</Label>
                <Input
                  id="edit-product-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.id} value={c.code}>
                        {c.code} - {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as "Product" | "Service")}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Product">Product</SelectItem>
                  <SelectItem value="Service">Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {accountingEnabled && ledgerAccounts.length > 0 ? (
              <div className="space-y-2">
                <Label htmlFor="edit-product-income-account">Income account</Label>
                <Select
                  value={incomeAccountId ?? DEFAULT_ACCOUNT}
                  onValueChange={(v) => setIncomeAccountId(v === DEFAULT_ACCOUNT ? null : v)}
                >
                  <SelectTrigger id="edit-product-income-account" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={DEFAULT_ACCOUNT}>Organisation default</SelectItem>
                    {ledgerAccounts.map((a) => (
                      <SelectItem key={a.id} value={a.id!}>
                        {a.displayId ? `${a.displayId} · ` : ""}
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Where this product's revenue posts when invoices are pushed to your accounting platform.
                </p>
              </div>
            ) : null}
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
